import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import GavelIcon from '@mui/icons-material/Gavel';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import api from '../services/api';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [expertiseAnchor, setExpertiseAnchor] = useState(null);
  const [servicesAnchor, setServicesAnchor] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'About', path: '/about' },
    { text: 'Case Studies', path: '/case-studies' },
    { text: 'Blog', path: '/blog' },
    { text: 'Testimonials', path: '/testimonials' },
    { text: 'Contact', path: '/contact' },
  ];

  const servicesList = [
    'Legal Consultation',
    'Court Representation',
    'Document Review',
    'Contract Drafting',
    'Mediation Services',
    'Legal Research',
    'Property Matters',
    'Consumer Disputes',
    'Cyber Law Cases',
  ];

  useEffect(() => {
    const fetchPracticeAreas = async () => {
      try {
        const response = await api.get('/practice-areas');
        setPracticeAreas(response.data.slice(0, 8)); // Limit to 8 for dropdown
      } catch (error) {
        console.error('Error fetching practice areas:', error);
      }
    };

    const checkAdmin = async () => {
      try {
        const res = await api.get('/auth/me');
        setIsAdmin(res.data?.role === 'admin');
      } catch {
        setIsAdmin(false);
      }
    };

    fetchPracticeAreas();
    checkAdmin();
  }, []);

  const handleExpertiseOpen = (event) => {
    setExpertiseAnchor(event.currentTarget);
  };

  const handleExpertiseClose = () => {
    setExpertiseAnchor(null);
  };

  const handleServicesOpen = (event) => {
    setServicesAnchor(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchor(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box
      sx={{
        width: { xs: 280, sm: 320 },
        pt: 2,
      }}
    >
      <Box sx={{ px: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <GavelIcon sx={{ mr: 1, color: 'secondary.main' }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.05em',
              fontSize: '0.9rem',
              color: 'primary.main',
            }}
          >
            ADVOCATE PRIYANKA SWAMI
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              +91 88006 56660
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem' }}>
            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              advpriyankaswami@gmail.com
            </Typography>
          </Box>
        </Box>
      </Box>
      <List sx={{ pt: 2 }}>
        {menuItems.slice(0, 2).map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
              }}
            />
          </ListItem>
        ))}
        <ListItem
          component={Link}
          to="/practice-areas"
          onClick={handleDrawerToggle}
          sx={{
            py: 1.5,
            px: 3,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemText
            primary="Our Expertise"
            primaryTypographyProps={{
              fontSize: '1rem',
              fontWeight: 500,
              color: 'text.primary',
            }}
          />
        </ListItem>
        <ListItem
          component={Link}
          to="/services"
          onClick={handleDrawerToggle}
          sx={{
            py: 1.5,
            px: 3,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemText
            primary="Our Service"
            primaryTypographyProps={{
              fontSize: '1rem',
              fontWeight: 500,
              color: 'text.primary',
            }}
          />
        </ListItem>
        {menuItems.slice(2).map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
              }}
            />
          </ListItem>
        ))}
        {isAdmin && (
          <ListItem
            component={Link}
            to="/admin/dashboard"
            onClick={handleDrawerToggle}
            sx={{
              py: 1.5,
              px: 3,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary="Admin Dashboard"
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
              }}
            />
          </ListItem>
        )}
        <Box sx={{ px: 3, py: 2, pt: 3 }}>
          <Button
            variant="contained"
            color="secondary"
            component={Link}
            to="/appointment"
            onClick={handleDrawerToggle}
            fullWidth
            sx={{
              py: 1.5,
              fontSize: '0.95rem',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Get Appointment
          </Button>
        </Box>
      </List>
    </Box>
  );

  return (
    <>
      {/* Top Contact Bar - Desktop Only */}
      {!isMobile && (
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: 0.75,
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: { lg: 3, xl: 4 },
                fontSize: '0.875rem',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  +91 88006 56660
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 16 }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  advpriyankaswami@gmail.com
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
          <Toolbar
            sx={{
              py: { xs: 1, sm: 1.25, md: 1.5 },
              px: 0,
              justifyContent: 'space-between',
              minHeight: { xs: 56, sm: 64, md: 70 },
            }}
            disableGutters
          >
            {/* Logo Section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                mr: { xs: 1, sm: 2, md: 4, lg: 6 },
                maxWidth: { xs: '60%', sm: 'auto' },
              }}
            >
              <GavelIcon
                sx={{
                  mr: { xs: 0.75, sm: 1 },
                  color: 'secondary.main',
                  fontSize: { xs: 20, sm: 22, md: 24, lg: 26 },
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="h6"
                component={Link}
                to="/"
                sx={{
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  fontSize: {
                    xs: '0.7rem',
                    sm: '0.8rem',
                    md: '0.9rem',
                    lg: '1rem',
                  },
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  '&:hover': {
                    color: 'primary.dark',
                  },
                }}
              >
                {isSmallMobile ? 'ADV. PRIYANKA SWAMI' : 'ADVOCATE PRIYANKA SWAMI'}
              </Typography>
            </Box>

            {/* Navigation Links - Center (Desktop/Tablet) */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: { lg: 0.5, xl: 0.75 },
                  mx: { lg: 2, xl: 3 },
                  overflowX: 'auto',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                }}
              >
                {menuItems.slice(0, 2).map((item) => (
                  <Button
                    key={item.text}
                    color="inherit"
                    component={Link}
                    to={item.path}
                    sx={{
                      px: { lg: 1, xl: 1.25 },
                      py: 0.5,
                      fontSize: { lg: '0.8rem', xl: '0.875rem' },
                      minWidth: 'auto',
                      textTransform: 'none',
                      fontWeight: 500,
                      color: location.pathname === item.path ? 'secondary.main' : 'text.primary',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: 'secondary.main',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}

                {/* Our Expertise Dropdown */}
                <Button
                  onClick={handleExpertiseOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    px: { lg: 1, xl: 1.25 },
                    py: 0.5,
                    fontSize: { lg: '0.8rem', xl: '0.875rem' },
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontWeight: 500,
                    color: expertiseAnchor ? 'secondary.main' : 'text.primary',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: 'secondary.main',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Our Expertise
                </Button>
                <Menu
                  anchorEl={expertiseAnchor}
                  open={Boolean(expertiseAnchor)}
                  onClose={handleExpertiseClose}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 250,
                      maxHeight: 400,
                      overflowY: 'auto',
                    },
                  }}
                >
                  <MenuItem
                    component={Link}
                    to="/practice-areas"
                    onClick={handleExpertiseClose}
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    View All Practice Areas
                  </MenuItem>
                  {practiceAreas.map((area) => (
                    <MenuItem
                      key={area._id}
                      component={Link}
                      to={`/practice-areas#${area._id}`}
                      onClick={handleExpertiseClose}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {area.title}
                    </MenuItem>
                  ))}
                </Menu>

                {/* Our Service Dropdown */}
                <Button
                  onClick={handleServicesOpen}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    px: { lg: 1, xl: 1.25 },
                    py: 0.5,
                    fontSize: { lg: '0.8rem', xl: '0.875rem' },
                    minWidth: 'auto',
                    textTransform: 'none',
                    fontWeight: 500,
                    color: servicesAnchor ? 'secondary.main' : 'text.primary',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: 'secondary.main',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Our Service
                </Button>
                <Menu
                  anchorEl={servicesAnchor}
                  open={Boolean(servicesAnchor)}
                  onClose={handleServicesClose}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 250,
                      maxHeight: 400,
                      overflowY: 'auto',
                    },
                  }}
                >
                  <MenuItem
                    component={Link}
                    to="/services"
                    onClick={handleServicesClose}
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    View All Services
                  </MenuItem>
                  {servicesList.map((service) => (
                    <MenuItem
                      key={service}
                      component={Link}
                      to="/services"
                      onClick={handleServicesClose}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {service}
                    </MenuItem>
                  ))}
                </Menu>

                {menuItems.slice(2).map((item) => (
                  <Button
                    key={item.text}
                    color="inherit"
                    component={Link}
                    to={item.path}
                    sx={{
                      px: { lg: 1, xl: 1.25 },
                      py: 0.5,
                      fontSize: { lg: '0.8rem', xl: '0.875rem' },
                      minWidth: 'auto',
                      textTransform: 'none',
                      fontWeight: 500,
                      color: location.pathname === item.path ? 'secondary.main' : 'text.primary',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: 'secondary.main',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}

            {/* CTA Button - Right (Desktop/Tablet) */}
            {!isMobile && (
              <Box sx={{ flexShrink: 0, ml: { lg: 2, xl: 3 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {isAdmin && (
                  <Button
                    variant="text"
                    color="primary"
                    component={Link}
                    to="/admin/dashboard"
                    sx={{
                      fontSize: { lg: '0.8rem', xl: '0.875rem' },
                      textTransform: 'none',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Admin Dashboard
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  component={Link}
                  to="/appointment"
                  sx={{
                    px: { lg: 2, xl: 2.5 },
                    py: { lg: 0.75, xl: 1 },
                    fontSize: { lg: '0.8rem', xl: '0.875rem' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(199, 146, 62, 0.3)',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(199, 146, 62, 0.4)',
                    },
                  }}
                >
                  Get Appointment
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  ml: 'auto',
                  color: 'primary.main',
                  p: { xs: 1, sm: 1.25 },
                }}
              >
                <MenuIcon sx={{ fontSize: { xs: 28, sm: 30 } }} />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: { xs: 280, sm: 320 },
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;

